import { Link } from "@inertiajs/react"
import { useTranslation } from "react-i18next"

import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs"
import {
  brandAnalyticsChannelPath,
  brandAnalyticsCrewPath,
  brandAnalyticsEmvPath,
  brandAnalyticsMissionsPath,
} from "@/routes"

/**
 * OKS-65 — Shared tab strip mounted above the `<h1>` on the
 * `/brand/analytics/*` surfaces. The active tab is URL-driven (caller
 * passes `active`); the `<TabsTrigger>`s render Inertia `<Link>` elements
 * via Base UI's `render` prop so the navigation is a full Inertia visit
 * (the URL pivot is what swaps the Inertia page, not local client tab state).
 *
 * OKS-503 extended the strip from 2 → 3 tabs (added **EMV** at
 * `/brand/analytics/emv`). OKS-734 extended it 3 → 4 (added **Channel** at
 * `/brand/analytics/channel`).
 *
 * # Why URL-driven, not client tab state
 *
 * The tabs render different Inertia pages
 * (`Brand/Analytics/Missions/Index` vs `Crew/Index` vs `Emv/Index` vs
 * `Channel/Index`). Client-state tabs would conflict with Inertia's
 * URL-as-truth model; a real-link `<Link>` swap keeps refresh / back /
 * share-URL flows correct.
 *
 * # i18n keys
 *
 * Labels read from `analytics.crew.tabs.{missions,crew,emv,channel}`. The
 * keys live under the OKS-65 `crew_analytics` locale namespace (shared
 * with OKS-63's `analytics.*` because they're sibling tabs on the same
 * surface).
 */
export type BrandAnalyticsTab = "missions" | "crew" | "emv" | "channel"

interface BrandAnalyticsTabsProps {
  active: BrandAnalyticsTab
}

export function BrandAnalyticsTabs({ active }: BrandAnalyticsTabsProps) {
  const { t } = useTranslation()

  return (
    <Tabs value={active} data-testid="brand-analytics-tabs">
      <TabsList>
        <TabsTrigger
          value="missions"
          render={<Link href={brandAnalyticsMissionsPath()} />}
        >
          {t("analytics.crew.tabs.missions")}
        </TabsTrigger>
        <TabsTrigger
          value="crew"
          render={<Link href={brandAnalyticsCrewPath()} />}
        >
          {t("analytics.crew.tabs.crew")}
        </TabsTrigger>
        <TabsTrigger
          value="emv"
          render={<Link href={brandAnalyticsEmvPath()} />}
        >
          {t("analytics.crew.tabs.emv")}
        </TabsTrigger>
        <TabsTrigger
          value="channel"
          render={<Link href={brandAnalyticsChannelPath()} />}
        >
          {t("analytics.crew.tabs.channel")}
        </TabsTrigger>
      </TabsList>
    </Tabs>
  )
}
